import type { TicketListItem } from '@uniticket/shared';
import { ticketCategoryRaw } from '@/constants/category-options';

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TYPE_LABELS: Record<string, string> = {
  OPEN_ORDER: 'Open Order',
  ADDRESS_MANAGEMENT: 'Address Management',
  SUPPORT_REQUEST: 'Support Request',
  BUILD: 'Build Management',
};

export function exportTicketsCsv(tickets: TicketListItem[], isInternal: boolean): void {
  const headers = [
    'Ticket #',
    'Type',
    'Subcategory',
    'Subject',
    'Status',
    'Priority',
    ...(isInternal ? ['Operator', 'Assigned To'] : []),
    'Created',
    'Updated',
    'Delta (days)',
    'Creator Name',
    'Creator Email',
    'Creator Group Name',
    'Last Updater Email',
    'City',
    'SLA Breached',
    'JVID',
    'DMP Order ID',
  ];

  const rows = tickets.map((t) => {
    const deltaMs = new Date(t.updatedAt).getTime() - new Date(t.createdAt).getTime();
    const deltaDays = Math.max(0, Math.floor(deltaMs / 86_400_000));
    return [
      t.ticketNumber,
      TYPE_LABELS[t.ticketType] ?? t.ticketType,
      // One Subcategory column per type, via the shared derivation the list column also uses, so
      // the two cannot drift. It now covers Support (category) and Address Management
      // ("Source / Action") as well, which were blank here because those fields never reached the
      // browser. The HEADER deliberately stays "Subcategory": it is a contract with the
      // spreadsheets people have already built on this export. Untranslated English, like the
      // rest of this file.
      ticketCategoryRaw(t)?.replace(/_/g, ' ') ?? '',
      t.subject,
      t.status.replace(/_/g, ' '),
      t.priority,
      ...(isInternal
        ? [t.operator.name, t.assignee?.displayName ?? '']
        : []),
      formatDate(t.createdAt),
      formatDate(t.updatedAt),
      String(deltaDays),
      t.createdBy?.displayName ?? '',
      t.creatorEmail ?? '',
      (t.creatorGroupNames ?? []).join(', '),
      t.lastUpdaterEmail ?? '',
      t.addrCity ?? '',
      t.slaBreached ? 'Yes' : 'No',
      t.jvid != null ? String(t.jvid) : '',
      t.dmpOrderId ?? '',
    ];
  });

  // BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const csv =
    BOM +
    headers.map(escapeCsvField).join(',') +
    '\n' +
    rows.map((row) => row.map(escapeCsvField).join(',')).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
